import { Loading, SearchSelectOptionProps } from "@alm/flow-ds/components";
import CustomLink from "components/CustomLink";
import { ListGroup, ListGroupItem } from "components/ListGroup";
import TaskCard from "components/TasksViews/TaskCard";
import TasksTableHeadings from "components/TasksViews/TasksTableHeadings";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import usePagedData from "hooks/usePagedData";
import { TasksManagerPayload } from "lib/api/services/workflow";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";
import React from "react";

interface Props {
	search: string;
	status?: "ACTIVE" | "COMPLETED" | "ALL";
	selectedCandidate: SearchSelectOptionProps;
	selectedAssignee: string;
	allManagerRoles: string[];
	allUserRoles: string[];
	setStatusValue: (value: string) => void;
	setCandidateValue: (value: SearchSelectOptionProps) => void;
	setAssigneeValue: (value: string) => void;
	requestTasks: (data: TasksManagerPayload) => Promise<{
		hasMore: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		results: any;
		totalCount: any;
	}>;
}

const TasksManagerTable = ({
	search,
	status,
	selectedCandidate,
	selectedAssignee,
	allManagerRoles,
	allUserRoles,
	setStatusValue,
	setCandidateValue,
	setAssigneeValue,
	requestTasks,
}: Props) => {
	const { t } = useTranslation();

	const history = useHistory();
	const location = useLocation();

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const [currentPage, setCurrentPage] = useState(1);

	const [total, setTotal] = useState<number | undefined>(0);

	const { paginationLimit } = useMobileView();

	const { state, setData, ready, loading, searching } = usePagedData();

	const disableOnLoad = state.loading ? "disabled" : "";

	const getMenuSelectors = useCallback(id => {
		return {
			menu: document.querySelector(`#tasksMenu__dropdown__${id}`),
			menuList: document.querySelector(
				`#tasksMenu__dropdown__${id} + div`
			),
		};
	}, []);

	const { limit, offset } = state;

	const getTasksPayload = useCallback(() => {
		const params = new URLSearchParams(location.search);

		let newRoles: string[] | undefined = undefined;
		let newUsers: string[] | undefined = undefined;
		let newAssignees: string[] | undefined = undefined;
		let newStatus: "ACTIVE" | "COMPLETED" | undefined = "ACTIVE";

		const paramRole = params.get("candidate_role");
		const paramUser = params.get("candidate_user");
		const paramAssignee = params.get("assignee");
		const paramStatus = params.get("status");

		if (paramRole !== null && paramRole !== "all_roles") {
			newRoles = [paramRole];
			setCandidateValue({ label: paramRole, value: paramRole });
		} else if (
			paramRole === "all_roles" ||
			selectedCandidate.value === "ALL_ROLES"
		) {
			newRoles = allManagerRoles;
			setCandidateValue({
				label: t("main.tasks.headings.allRoles"),
				value: "ALL_ROLES",
			});
		}

		if (paramUser !== null && paramUser !== "all_users") {
			newUsers = [paramUser];
			setCandidateValue({ label: paramUser, value: paramUser });
		} else if (paramUser === "all_users") {
			newUsers = allUserRoles;
			setCandidateValue({
				label: t("main.tasks.headings.allUsers"),
				value: "ALL_USERS",
			});
		}

		if (paramAssignee !== null && paramAssignee !== "all") {
			newAssignees = [paramAssignee];
			setAssigneeValue(paramAssignee);
		} else if (paramAssignee === "all") {
			setAssigneeValue("ALL");
		}

		if (paramStatus === "active") {
			newStatus = "ACTIVE";
			setStatusValue(newStatus);
		}
		if (paramStatus === "completed") {
			newStatus = "COMPLETED";
			setStatusValue(newStatus);
		}
		if (paramStatus === "all") {
			newStatus = undefined;
			setStatusValue("ALL");
		}

		const payload: TasksManagerPayload = {
			roles: newRoles,
			users: newUsers,
			assignees: newAssignees,
			status: newStatus,
			search: search,
			limit: 10,
			offset: 0,
		};

		return payload;
	}, [
		location.search,
		selectedCandidate.value,
		search,
		setCandidateValue,
		allManagerRoles,
		t,
		allUserRoles,
		setAssigneeValue,
		setStatusValue,
	]);

	/**
	 * Checks for clicks on the instance item to see if it's not the
	 * instance menu dropdown
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleOnClick = useCallback(
		(e, id) => {
			e.preventDefault();
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);

			if (
				!selectors.menu?.contains(e.target) &&
				!selectors.menuList?.contains(e.target)
			) {
				addSentryBreadcrumb({
					category: "click",
					message: `Task ${id}`,
				});
				history.push({
					pathname: `/manager/task/${id}`,
					search: `${history.location.search}`,
					state: { from: location.pathname },
				});
			}
		},
		[getMenuSelectors, history, location.pathname]
	);

	/**
	 * Checks if key is spacebar or enter
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent, id: string) => {
			const target = e.target as HTMLElement;
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);

			// If key is spacebar or enter and target isn't instance dropdown then go to instance
			if (
				(e.key === "Enter" || e.keyCode === 32) &&
				!selectors.menu?.contains(target) &&
				!selectors.menuList?.contains(target)
			) {
				addSentryBreadcrumb({
					category: "keydown",
					message: `Task ${id}`,
				});
				e.preventDefault();
				history.push({
					pathname: `/manager/task/${id}`,
					search: `${history.location.search}`,
				});
			}
		},
		[getMenuSelectors, history]
	);

	/**
	 * Load tasks from mount
	 */
	useEffect(() => {
		if (!init) {
			// Load tasks from API
			requestTasks(getTasksPayload())
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
						setInit(true);
					}
				})
				.catch(e => console.error(e));
		}
	}, [_isMounted, init, ready, setData, requestTasks, getTasksPayload]);

	useEffect(() => {
		if (init) {
			searching();
			setCurrentPage(1);
			requestTasks(getTasksPayload())
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, status, selectedCandidate.value, selectedAssignee]);

	const pageData = useCallback(
		(page: number) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: "Tasks page",
			});
			loading();

			const candidateValue = selectedCandidate.value;

			const payload: TasksManagerPayload = {
				roles:
					selectedCandidate.value === "ALL_ROLES"
						? allManagerRoles
						: allManagerRoles.includes(candidateValue)
						? [candidateValue]
						: undefined,
				users:
					selectedCandidate.value === "ALL_USERS"
						? allUserRoles
						: allUserRoles.includes(candidateValue)
						? [candidateValue]
						: undefined,
				assignees:
					selectedAssignee === "ALL" ? undefined : [selectedAssignee],
				status:
					status !== "ACTIVE" && status !== "COMPLETED"
						? undefined
						: status,
				limit,
				offset: (page - 1) * limit,
				search: state.search === null ? "" : state.search,
			};

			requestTasks(payload)
				.then(res => {
					setData(res);
					ready();
				})
				.catch(e => console.error(e));
			setCurrentPage(page);
		},
		[
			loading,
			selectedCandidate.value,
			allManagerRoles,
			allUserRoles,
			selectedAssignee,
			status,
			limit,
			state.search,
			requestTasks,
			ready,
			setData,
		]
	);

	if (!init || state.searching)
		return <Loading className="mt-4 elem__loader--md" />;
	return (
		<>
			{state.results?.length ? (
				<>
					<TasksTableHeadings className="tasks__heading--managers" />
					<ListGroup className="list-group-links">
						{state.results.map((task, index: number) => (
							<ListGroupItem
								className={`task-card task-card--claimed task-card--read ${disableOnLoad}`}
								key={`task__${index}`}>
								<CustomLink
									handleClick={(e: React.MouseEvent) =>
										handleOnClick(e, task.id)
									}
									handleKeyDown={(e: React.KeyboardEvent) =>
										handleKeyDown(e, task.id)
									}
									label={task.id}
									to={`/manager/task/${task.id}`}>
									<TaskCard {...task} />
								</CustomLink>
							</ListGroupItem>
						))}
					</ListGroup>
				</>
			) : (
				!state.loading && (
					<p className="no-tasks">
						{t(
							search.trim()
								? "main.tasks.text.noSearch"
								: "main.tasks.text.noTasks"
						)}
					</p>
				)
			)}
			{total !== undefined && total > limit && !state.searching && (
				<PaginationBar
					currentPage={currentPage}
					limit={limit}
					total={total}
					paginationLimit={paginationLimit}
					onPageChange={page => {
						pageData(page);
					}}
					loading={state.loading}
				/>
			)}
		</>
	);
};

export default TasksManagerTable;
